import { CSSResultGroup, html, css, TemplateResult, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, state } from 'lit/decorators.js';

import '../ghs-icon/ghs-icon';
import componentStyles from './ghs-quote-testimonial.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotController } from '../../controllers/slot-controller';

export type QuoteVariant = 'default' | 'plain';

/**
 * The quote-testimonial component.
 * @element ghs-quote-testimonial
 */

/**
 * @note
 * This component wants to be fed a square cropped image.
 */

@customElement('ghs-quote-testimonial')
export class GhsQuoteTestimonial extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, css``];

  slots = new SlotController(this);

  @state() hasCtaSlot: boolean;
  @state() hasImageSlot: boolean;
  @state() hasTitleSlot: boolean;
  @state() hasAttributionSlot: boolean;

  /**
   * Quote variant.
   */
  @property() variant: QuoteVariant = 'default';

  /**
   * Attribution text.
   */
  @property() attribution: string;

  @property({ type: Boolean, attribute: 'full-width' })
  fullWidth = false;

  firstUpdated(): void {
    super.firstUpdated();
    this.hasCtaSlot = this.slots.test('quote-cta');
    this.hasImageSlot = this.slots.test('quote-hero-image');
    this.hasTitleSlot = this.slots.test('quote-title');
    this.hasAttributionSlot = this.slots.test('quote-attribution');
  }

  renderIcon(): TemplateResult {
    return this.variant === 'plain' || this.site === 'BH'
      ? html` <ghs-icon
          name="${this.site === 'BH' ? 'quote-square' : 'quote'}"
          size="50px"
          mobile-size="25px"
          class="quote-testimonial__eyebrow"
          site=${this.site}
        ></ghs-icon>`
      : html` <ghs-icon
          name="double-quote"
          size="100px"
          mobile-size="80px"
          class="quote-testimonial__eyebrow"
          site=${this.site}
        ></ghs-icon>`;
  }

  renderAttribution() {
    return this.attribution
      ? html`
          <div class="quote-testimonial__attribution">
            <hr />
            ${this.attribution}
          </div>
        `
      : nothing;
  }

  render(): TemplateResult {
    const classes = {
      'quote-testimonial': true,
      'quote-testimonial--full-width': this.fullWidth,
      [`quote-testimonial--${this.variant}`]: true,
      'quote-testimonial--has-image': this.hasImageSlot,
    };

    return html`
      <div class=${classMap(classes)}>
        <div class="quote-testimonial__inner">
          ${this.site !== 'BH' ? this.renderIcon() : nothing}
          <div class="quote-testimonial__content-section">
            ${this.site === 'BH' ? this.renderIcon() : nothing}
            ${this.hasTitleSlot
              ? html`
                  <div class="quote-testimonial__title">
                    <slot name="quote-title"></slot>
                  </div>
                `
              : nothing}
            <div class="quote-testimonial__content">
              <slot name="quote-content"></slot>
            </div>

            ${this.renderAttribution()}
          </div>

          ${this.hasImageSlot
            ? html`
                <div class="quote-testimonial__image">
                  <slot name="quote-hero-image"></slot>
                </div>
              `
            : nothing}
        </div>

        ${this.hasCtaSlot
          ? html`
              <div class="quote-testimonial__cta">
                <slot name="quote-cta"></slot>
              </div>
            `
          : nothing}
      </div>
    `;
  }
}
